import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faArrowsLeftRight, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import './styles/CustomizedVariable.css';
import './styles/PropertiesComponent.css';

let _rowId = 0;
const blankRow = () => ({ _id: ++_rowId, variableName: '', row: '', col: '' });

/**
 * Moves one or more variables' data out of table_single and into
 * table_grid_single or table_grid_multiple (each assigned a row, and a
 * column too for table_grid_multiple), re-pointing their vtm entries to the
 * new table. Backend: workRoutes.py's /move-variable-to-grid, which widens
 * the destination's value column type (int/float/text) to fit whatever the
 * batch actually needs instead of forcing a cast that could fail or truncate.
 */
const MoveVariableToGridModal = ({ isOpen, onClose, projectId, user }) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [targetTable, setTargetTable] = useState('table_grid_single');
  const [rows, setRows] = useState([blankRow()]);
  const [moving, setMoving] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const isMultiple = targetTable === 'table_grid_multiple';

  const reset = () => {
    setTargetTable('table_grid_single');
    setRows([blankRow()]);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addRow = () => setRows((r) => [...r, blankRow()]);
  const removeRow = (id) => setRows((r) => r.filter((x) => x._id !== id));
  const updateRow = (id, field, value) =>
    setRows((r) => r.map((x) => (x._id === id ? { ...x, [field]: value } : x)));

  const handlePaste = (e) => {
    const text = e.target.value;
    if (!text.trim()) return;
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const parsed = lines.map((line) => {
      const parts = line.split('\t').map((p) => p.trim());
      return { _id: ++_rowId, variableName: parts[0] || '', row: parts[1] || '', col: parts[2] || '' };
    }).filter((r) => r.variableName);
    if (parsed.length > 0) {
      setRows((r) => [...r.filter((x) => x.variableName || x.row || x.col), ...parsed]);
      e.target.value = '';
    }
  };

  const validRows = rows.filter((r) => r.variableName.trim() && r.row.trim() && (!isMultiple || r.col.trim()));

  const handleMove = async () => {
    if (validRows.length === 0) {
      toast.warning(isMultiple
        ? 'Add at least one variable with a variable name, row and column'
        : 'Add at least one variable with a variable name and row');
      return;
    }

    const confirmed = await confirm({
      title: 'Move Variable(s)',
      message: `This will move ${validRows.length} variable(s) out of table_single into ${targetTable}, dropping each column from table_single. Proceed?`,
      confirmText: 'Move',
    });
    if (!confirmed) return;

    setMoving(true);
    setResult(null);
    try {
      const { data } = await apiService.post('/move-variable-to-grid', {
        projectId,
        targetTable,
        moves: validRows.map((r) => ({
          variableName: r.variableName.trim(),
          row: r.row.trim(),
          col: isMultiple ? r.col.trim() : undefined,
        })),
        user: user || '',
      });
      if (!data?.success) throw new Error(data?.error || 'Move failed');
      setResult(data);
      toast.success(data.message || 'Variable(s) moved');
    } catch (error) {
      toast.error(error.message || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--above-settings" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>

        <div className="modal-header">
          <h3>Move Variable(s) to Grid</h3>
          <button onClick={handleClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="variable-name-input">
          <label>Move To:</label>
          <select value={targetTable} onChange={(e) => { setTargetTable(e.target.value); setResult(null); }}>
            <option value="table_grid_single">table_grid_single</option>
            <option value="table_grid_multiple">table_grid_multiple</option>
          </select>
        </div>

        <div className="cr-paste-area" style={{ marginTop: 8 }}>
          <label className="cr-paste-label">
            Paste from Excel (tab-separated: variable name | row{isMultiple ? ' | col' : ''} — one per line)
          </label>
          <textarea
            className="cr-paste-textarea"
            placeholder={isMultiple ? 'Q1r1\t1\t1\nQ1r2\t2\t1' : 'Q1r1\t1\nQ1r2\t2'}
            onChange={handlePaste}
          />
        </div>

        <table className="cr-table">
          <thead>
            <tr>
              <th className="cr-th cr-th-label">Variable Name</th>
              <th className="cr-th" style={{ width: '18%' }}>Row</th>
              {isMultiple && <th className="cr-th" style={{ width: '18%' }}>Column</th>}
              <th className="cr-th cr-th-del"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="cr-row">
                <td className="cr-td">
                  <input
                    type="text"
                    className="cr-input cr-input-label"
                    value={r.variableName}
                    placeholder="e.g. Q1r1"
                    onChange={(e) => updateRow(r._id, 'variableName', e.target.value)}
                  />
                </td>
                <td className="cr-td">
                  <input
                    type="text"
                    className="cr-input cr-input-index"
                    value={r.row}
                    placeholder="1"
                    onChange={(e) => updateRow(r._id, 'row', e.target.value)}
                  />
                </td>
                {isMultiple && (
                  <td className="cr-td">
                    <input
                      type="text"
                      className="cr-input cr-input-index"
                      value={r.col}
                      placeholder="1"
                      onChange={(e) => updateRow(r._id, 'col', e.target.value)}
                    />
                  </td>
                )}
                <td className="cr-td">
                  <button className="cr-del-btn" title="Remove" onClick={() => removeRow(r._id)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 8 }}>
          <button className="cr-add-btn" onClick={addRow}>+ Add Row</button>
        </div>

        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
          fontSize: 12.5, lineHeight: 1.5, marginTop: 10, background: '#f9fafb', color: '#374151',
        }}>
          Moves every stored value for each variable from <code>table_single</code> into {targetTable} under
          its given row{isMultiple ? '/column' : ''}, then drops the column from <code>table_single</code> and
          updates the variable map (vtm). The destination's value column is automatically widened
          (int → float → text) to fit whatever type the moved variable(s) actually need — string-valued
          variables won't fail or get truncated.
        </div>

        {result && (
          <div style={{ marginTop: 12, fontSize: 12.5 }}>
            <div><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />{' '}
              {result.totalMoved} value(s) moved across {Object.keys(result.moved || {}).length} variable(s)
              — value column: <strong>{result.valueColumnType}</strong></div>
            {result.skipped?.length > 0 && (
              <div style={{ marginTop: 4, color: '#b45309' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> Skipped: {result.skipped.map((s) => `${s.name} (${s.reason})`).join('; ')}
              </div>
            )}
            {result.errors?.length > 0 && (
              <div style={{ marginTop: 4, color: '#dc2626' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {result.errors.join('; ')}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <div className="footer-buttons">
            <button className="add-row-btn" onClick={handleClose} disabled={moving}>
              Close
            </button>
            <button className="save-btn" onClick={handleMove} disabled={moving}>
              {moving
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Moving…</>
                : <><FontAwesomeIcon icon={faArrowsLeftRight} /> Move {validRows.length || ''}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoveVariableToGridModal;
